import BrowserInteractionTime from "browser-interaction-time";

const HEARTBEAT_MS = 2 * 1000;
const HEARTBEAT_CHECK_MS = 100;
const IDLE_DETECTION_DISABLED_MS = Number.MAX_SAFE_INTEGER;

function readMeta(name) {
    const el = document.querySelector(`meta[name="${name}"]`);
    return el ? el.getAttribute("content") || "" : "";
}

function viewerIsSeller() {
    return readMeta("viewer-is-seller") === "1";
}

function generateSessionUuid() {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }

    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
        const random = (Math.random() * 16) | 0;
        const value = char === "x" ? random : (random & 0x3) | 0x8;
        return value.toString(16);
    });
}

export function start({ quoteKey }) {
    if (!("sendBeacon" in navigator)) {
        return null;
    }

    if (viewerIsSeller()) {
        return null;
    }

    const endpoint = `/api/checkout/quotes/${encodeURIComponent(quoteKey)}/view-time`;
    const sessionUuid = generateSessionUuid();
    let lastReportedSeconds = 0;

    const packageMs = {};
    let activePackageId = null;
    let activePackageStartMs = 0;

    const finalizeActivePackage = (nowMs) => {
        if (activePackageId === null) {
            return;
        }
        packageMs[activePackageId] = (packageMs[activePackageId] || 0) + Math.max(0, nowMs - activePackageStartMs);
        activePackageId = null;
    };

    const packageSecondsSnapshot = (nowMs) => {
        const totals = { ...packageMs };
        if (activePackageId !== null) {
            totals[activePackageId] = (totals[activePackageId] || 0) + Math.max(0, nowMs - activePackageStartMs);
        }

        const seconds = {};
        for (const [packageId, ms] of Object.entries(totals)) {
            const rounded = Math.round(ms / 1000);
            if (rounded > 0) {
                seconds[packageId] = rounded;
            }
        }
        return seconds;
    };

    const report = (timeInMs) => {
        const activeSeconds = Math.round(timeInMs / 1000);
        if (activeSeconds <= 0 || activeSeconds === lastReportedSeconds) {
            return;
        }
        lastReportedSeconds = activeSeconds;
        const payload = new URLSearchParams({
            session_uuid: sessionUuid,
            active_seconds: String(activeSeconds),
        });
        for (const [packageId, seconds] of Object.entries(packageSecondsSnapshot(timeInMs))) {
            payload.append(`packages[${packageId}]`, String(seconds));
        }
        navigator.sendBeacon(endpoint, payload);
    };

    const timer = new BrowserInteractionTime({
        stopTimerOnTabchange: true,
        idleTimeoutMs: IDLE_DETECTION_DISABLED_MS,
        checkCallbacksIntervalMs: HEARTBEAT_CHECK_MS,
        timeIntervalEllapsedCallbacks: [
            {
                timeInMilliseconds: HEARTBEAT_MS,
                callback: report,
                multiplier: (threshold) => threshold + HEARTBEAT_MS,
            },
        ],
        browserTabInactiveCallbacks: [report],
    });

    if (document.visibilityState === "visible") {
        timer.startTimer();
    }

    document.addEventListener("quote-package-view-start", (event) => {
        const packageId = Number(event.detail?.packageId);
        if (!packageId) {
            return;
        }
        const nowMs = timer.getTimeInMilliseconds();
        finalizeActivePackage(nowMs);
        activePackageId = packageId;
        activePackageStartMs = nowMs;
    });

    document.addEventListener("quote-package-view-end", (event) => {
        const packageId = Number(event.detail?.packageId);
        if (!packageId || packageId !== activePackageId) {
            return;
        }
        finalizeActivePackage(timer.getTimeInMilliseconds());
    });

    // Opening the checkout flushes what has been measured so far, because the
    // customer may complete and leave from inside the iframe. The timer keeps
    // running afterwards: for document quotes the checkout modal is the
    // primary interaction, and tearing the timer down here used to discard
    // every second the visit spent after it was first opened.
    document.addEventListener("quote-checkout-iframe-open", () => {
        const nowMs = timer.getTimeInMilliseconds();
        finalizeActivePackage(nowMs);
        report(nowMs);
    });

    window.addEventListener("pagehide", () => report(timer.getTimeInMilliseconds()));

    return timer;
}
